<script lang="ts">
	import { createEventDispatcher, onMount } from 'svelte';
	import type { Column } from 'FilterTable';
	import DataTable, { Head, Cell, Row, Body, Pagination } from '@smui/data-table/styled';
	import Textfield from '@smui/textfield/styled';
	import Select from 'svelte-select';
	import IconButton from '@smui/icon-button/styled';

	const dispatch = createEventDispatcher();

	export let columns: Column[];

	export let rows: any;
	export let sortBy = '';
	export let sortOrder = 1;
	export let iconAsc = '▲';
	export let iconDesc = '▼';
	export let filterSelections = {};

	let rowsPerPage = 25;
	let currentPage = 0;

	$: if (currentPage > lastPage) {
		currentPage = lastPage;
	}

	$: start = currentPage * rowsPerPage;
	$: end = Math.min(start + rowsPerPage, c_rows.length);
	$: slice = c_rows.slice(start, end);
	$: lastPage = Math.max(Math.ceil(c_rows.length / rowsPerPage) - 1, 0);

	let sortFunction: (r: any) => string = () => '';
	let showFilterHeader = columns.some((c: Column) => {
		return c.filterOptions !== undefined || c.searchValue !== undefined;
	});
	let filterValues: { [key: string]: { name: string; value: string }[] } = {};
	let columnByKey = {};

	columns.forEach((col: Column) => {
		columnByKey[col.key] = col;
	});

	$: c_rows = rows
		.filter((r: any) => {
			// get search and filter results/matches
			return Object.keys(filterSelections).every((f) => {
				// check search (text input) matches
				let resSearch =
					filterSelections[f] === '' ||
					(columnByKey[f].searchValue &&
						(columnByKey[f].searchValue(r) + '')
							.toLocaleLowerCase()
							.indexOf((filterSelections[f] + '').toLocaleLowerCase()) >= 0);

				// check filter (dropdown) matches
				let resFilter =
					resSearch ||
					filterSelections[f] === undefined ||
					// default to value() if filterValue() not provided in col
					filterSelections[f] ===
						(typeof columnByKey[f].filterValue === 'function'
							? columnByKey[f].filterValue(r)
							: columnByKey[f].value(r));
				return resFilter;
			});
		})
		.map((r: any) => Object.assign({}, r, { $sortOn: sortFunction(r) }))
		.sort((a: any, b: any) => {
			if (a.$sortOn > b.$sortOn) return sortOrder;
			else if (a.$sortOn < b.$sortOn) return -sortOrder;
			return 0;
		});

	const asStringArray = (v: string | string[]) =>
		[]
			.concat(v)
			.filter((v) => typeof v === 'string' && v !== '')
			.join(' ');

	const calculateFilterValues = () => {
		filterValues = {};
		columns.forEach((c: Column) => {
			if (typeof c.filterOptions === 'function') {
				//@ts-ignore
				filterValues[c.key] = c.filterOptions(rows);
			} else if (Array.isArray(c.filterOptions)) {
				// if array of strings is provided, use it for name and value
				filterValues[c.key] = c.filterOptions.map((val: any) => ({ name: val, value: val }));
			}
		});
	};

	$: {
		let col = columnByKey[sortBy];
		if (col !== undefined && col.sortable === true && typeof col.value === 'function') {
			sortFunction = (r: any) => col.value(r);
		}
	}

	$: {
		// if filters are enabled, watch rows and columns
		if (showFilterHeader && columns && rows) {
			calculateFilterValues();
		}
	}

	const updateSortOrder = (colKey: any) => {
		if (colKey === sortBy) {
			sortOrder = sortOrder === 1 ? -1 : 1;
		} else {
			sortOrder = 1;
		}
	};

	const handleClickCol = (event: Event, col: Column) => {
		updateSortOrder(col.key);
		sortBy = col.key;
		dispatch('clickCol', { event, col, key: col.key });
	};

	const handleClickRow = (event: Event, row: any) => {
		dispatch('clickRow', { event, row });
	};

	const handleClickCell = (event: Event, row: any, key: string) => {
		dispatch('clickCell', { event, row, key });
	};

	function dispatchRefresh() {
		dispatch('refresh');
	}

	onMount(() => {
		document.querySelectorAll('.mdc-text-field input:last-child').forEach((input) => {
			input.parentNode.removeChild(input);
		});
	});
</script>

<DataTable style="width: calc(100% - 10px);">
	<Head>
		{#if showFilterHeader}
			<Row>
				{#each columns as col}
					<Cell>
						{#if col.searchValue !== undefined}
							<div>
								<Textfield class="fullwidth" variant="outlined" value={''}>
									<input
										class="mdc-text-field__input"
										type="text"
										bind:value={filterSelections[col.key]}
									/>
								</Textfield>
							</div>
						{:else if filterValues[col.key] !== undefined}
							<Select
								selectedValue={{ value: '', label: '' }}
								items={filterValues[col.key].map((option) => {
									return { value: option.value, label: option.name };
								})}
								on:select={(e) => {
									filterSelections[col.key] = e.detail.value;
								}}
								on:clear={() => {
									filterSelections[col.key] = undefined;
								}}
							/>
							<!--<select bind:value={filterSelections[col.key]} class={asStringArray(classNameSelect)}>
								<option value={''} />
								{#each filterValues[col.key] as option}
									<option
										value={//@ts-ignore
										option.value}>{option.name}</option
									>
								{/each}
							</select>-->
						{/if}
					</Cell>
				{/each}
			</Row>
		{/if}

		<slot name="header" {sortOrder} {sortBy}>
			<Row>
				{#each columns as col}
					{#if col.sortable}
						<Cell
							on:click={(e) => handleClickCol(e, col)}
							class={asStringArray(['isSortable', col.headerClass])}
						>
							<div style="white-space: nowrap">
								{col.title}{#if sortBy === col.key}&nbsp;{sortOrder === 1 ? iconAsc : iconDesc}{/if}
							</div>
						</Cell>
					{:else}
						<Cell class={col.headerClass}>
							{col.title}

							{#if sortBy === col.key}
								{sortOrder === 1 ? iconAsc : iconDesc}
							{/if}
						</Cell>
					{/if}
				{/each}
			</Row>
		</slot>
	</Head>
	<Body style="border: 0">
		{#each slice as row, n}
			<slot name="row" {row} {n}>
				<Row
					on:click={(e) => {
						handleClickRow(e, row);
					}}
				>
					{#each columns as col}
						<Cell
							on:click={(e) => {
								handleClickCell(e, row, col.key);
							}}
						>
							{#if col.renderComponent}
								<svelte:component
									this={col.renderComponent.component || col.renderComponent}
									{...col.renderComponent.props || {}}
									{row}
									{col}
									on:refresh={dispatchRefresh}
								/>
							{:else}
								{@html col.renderValue ? col.renderValue(row) : col.value(row)}
							{/if}
						</Cell>
					{/each}
				</Row>
			</slot>
		{/each}
	</Body>

	<Pagination slot="paginate">
		<svelte:fragment slot="total">
			{start + 1}-{end} of {c_rows.length}
		</svelte:fragment>

		<IconButton
			class="material-icons"
			on:click={() => (currentPage = 0)}
			disabled={currentPage === 0}>first_page</IconButton
		>
		<IconButton class="material-icons" on:click={() => currentPage--} disabled={currentPage === 0}
			>chevron_left</IconButton
		>
		<IconButton
			class="material-icons"
			on:click={() => currentPage++}
			disabled={currentPage === lastPage}>chevron_right</IconButton
		>
		<IconButton
			class="material-icons"
			on:click={() => (currentPage = lastPage)}
			disabled={currentPage === lastPage}>last_page</IconButton
		>
	</Pagination>
</DataTable>

<style lang="scss">
	:global(.mdc-data-table__row:not(.mdc-data-table__row--selected):hover) {
		background-color: #f9f9f9;
	}

	:global(.mdc-data-table__header-cell) {
		overflow: visible;
	}

	:global(.mdc-data-table__cell),
	:global(.mdc-data-table__table) {
		white-space: normal;
	}

	:global(.mdc-data-table .mdc-text-field--outlined) {
		height: 42px;
	}

	:global(.interest-list-chip) {
		margin: 1px !important;
		height: auto !important;
	}

	:global(.interest-list-chiptext) {
		white-space: normal !important;
		font-size: 0.7rem !important;
	}

	:global(.isSortable) {
		cursor: pointer !important;
	}

	input {
		max-width: 100%;
	}
</style>
